import { PERMISSIONS } from '@helios/authz';
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type RefetchOptions } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/authContext';
import { settingsClient, type NotificationQuery, type UpdateFeatureFlagInput, type UpdateOrganizationSettingsInput, type UpdateUserSettingsInput, type UserSettings } from '@/services/settingsClient';
import type { RequestOptions } from '@/services/apiClient';
import { sessionScopeKey, useSessionScopeFence, type SessionScopeIdentity } from './useSessionScope';

export interface AuditLogQuery { page: number; pageSize?: number; tenantId?: string; action?: string; entityType?: string }
export type SettingsQueryIdentity = SessionScopeIdentity;
export const settingsKeys = {
  bootstrap: ['settings', 'bootstrap'] as const,
  bootstrapFor: (identity: SettingsQueryIdentity) => ['settings', 'bootstrap', ...sessionScopeKey(identity)] as const,
  ownFor: (identity: SettingsQueryIdentity) => ['settings', 'me', ...sessionScopeKey(identity)] as const,
  notifications: ['settings', 'notifications'] as const,
  notificationsFor: (identity: SettingsQueryIdentity, query: NotificationQuery = { page: 1, pageSize: 8 }) => ['settings', 'notifications', ...sessionScopeKey(identity), query] as const,
  notificationSummaryFor: (identity: SettingsQueryIdentity) => ['settings', 'notifications', ...sessionScopeKey(identity), 'summary'] as const,
  auditLogs: (identity: SettingsQueryIdentity, query: AuditLogQuery) => ['settings', 'audit-logs', ...sessionScopeKey(identity), query] as const,
};

function useSettingsRead<T>(permission: string | undefined, key: (identity: SettingsQueryIdentity) => readonly unknown[], loader: (options: RequestOptions) => Promise<T>, interval = 60_000) {
  const auth = useAuth();
  const { identity, enabled: scoped, isCurrent } = useSessionScopeFence(permission);
  const enabled = scoped && auth.accessMode !== 'COMPANY_SETUP';
  const query = useQuery({
    queryKey: key(identity), enabled, placeholderData: () => undefined,
    queryFn: async ({ signal }) => {
      if (!enabled || !isCurrent(identity)) throw new Error('This settings view requires current authenticated access');
      const result = await loader({ signal, sessionOrigin: identity.sessionOrigin });
      signal.throwIfAborted();
      if (!isCurrent(identity)) throw new Error('Settings access changed while this view was loading.');
      return result;
    },
    staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: 'always', refetchInterval: interval, retry: false,
  });
  const runRefetch = query.refetch;
  const refetch = useCallback(async (options?: RefetchOptions) => {
    if (!enabled || !isCurrent(identity)) throw new Error('Current authenticated settings access is required.');
    const result = await runRefetch(options);
    if (!isCurrent(identity)) throw new Error('Settings access changed during refresh.');
    return { ...result, data: result.isError ? undefined : result.data };
  }, [enabled, identity, isCurrent, runRefetch]);
  return { ...query, refetch, data: enabled && !query.isError ? query.data : undefined };
}

export function useSettingsBootstrap() {
  return useSettingsRead(PERMISSIONS.SETTINGS_MANAGE, settingsKeys.bootstrapFor, settingsClient.getSettingsBootstrap);
}
export function useOwnSettings() {
  return useSettingsRead(undefined, settingsKeys.ownFor, settingsClient.getOwnSettings);
}
export function useAuditLogs(query: AuditLogQuery) {
  return useSettingsRead(PERMISSIONS.AUDIT_LOGS_READ, (identity) => settingsKeys.auditLogs(identity, query), (options) => settingsClient.listAuditLogs(query, options));
}
export function useSettingsNotifications(query: NotificationQuery = { page: 1, pageSize: 8 }) {
  const notifications = useSettingsRead(PERMISSIONS.NOTIFICATIONS_READ, (identity) => settingsKeys.notificationsFor(identity, query), (options) => settingsClient.listNotifications(query, options));
  const summary = useSettingsRead(PERMISSIONS.NOTIFICATIONS_READ, settingsKeys.notificationSummaryFor, settingsClient.getNotificationsSummary);
  const refetchList = notifications.refetch;
  const refetchSummary = summary.refetch;
  const refetch = useCallback(async (options?: RefetchOptions) => {
    const [list] = await Promise.all([refetchList(options), refetchSummary({ ...options, throwOnError: false })]);
    return list;
  }, [refetchList, refetchSummary]);
  return { ...notifications, summary: summary.data, summaryError: summary.error, summaryLoading: summary.isLoading, refetch };
}

function matchesIdentity(key: readonly unknown[], origin: SettingsQueryIdentity) {
  return key[0] === 'settings' && sessionScopeKey(origin).every((part, index) => key[index + 2] === part);
}
function useSettingsMutation<TInput, TOutput>(permission: string | undefined, action: (input: TInput, options: RequestOptions) => Promise<TOutput>, onSuccess?: (data: TOutput, origin: SettingsQueryIdentity) => void) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['settings-mutation', ...sessionScopeKey(identity)], retry: false,
    onMutate: () => identity,
    mutationFn: async (input: TInput) => {
      if (!enabled || !isCurrent(identity)) throw new Error('Settings access changed. Reload this page before saving.');
      try {
        const result = await action(input, { sessionOrigin: identity.sessionOrigin });
        if (!isCurrent(identity)) throw new Error('The originating session changed. Reload to check the save outcome.');
        return result;
      } catch (error) {
        // eslint-disable-next-line preserve-caught-error -- Prior-session errors must not escape through Error.cause after access changes.
        if (!isCurrent(identity)) throw new Error('The originating session changed. Reload to check the save outcome.');
        throw error;
      }
    },
    onSuccess: async (data, _input, origin) => {
      if (!origin || !isCurrent(origin)) return;
      onSuccess?.(data, origin);
      await client.invalidateQueries({ predicate: (query) => matchesIdentity(query.queryKey, origin) });
    },
  });
  const visible = mutation.context && isCurrent(mutation.context);
  return { ...mutation, data: visible ? mutation.data : undefined, error: visible ? mutation.error : null, isPending: Boolean(visible && mutation.isPending) };
}
export function useUpdateUserSettings() {
  const auth = useAuth();
  const client = useQueryClient();
  return useSettingsMutation<UpdateUserSettingsInput, UserSettings>(undefined, settingsClient.updateUserSettings, (profile, origin) => {
    client.setQueryData(settingsKeys.ownFor(origin), profile);
    auth.applyOwnProfile(origin, profile);
  });
}
export function useUpdateOrganizationSettings() {
  return useSettingsMutation(PERMISSIONS.SETTINGS_MANAGE, (input: UpdateOrganizationSettingsInput, options) => settingsClient.updateOrganizationSettings(input, options));
}
export function useUpdateFeatureFlag() {
  return useSettingsMutation(PERMISSIONS.FEATURE_FLAGS_UPDATE, ({ key, input }: { key: string; input: UpdateFeatureFlagInput }, options) => settingsClient.updateFeatureFlag(key, input, options));
}
export function useNotificationMutations() {
  return {
    markRead: useSettingsMutation(PERMISSIONS.NOTIFICATIONS_UPDATE, settingsClient.markNotificationRead),
    markAllRead: useSettingsMutation(PERMISSIONS.NOTIFICATIONS_UPDATE, (_input: void, options) => settingsClient.markAllNotificationsRead(options)),
    createTest: useSettingsMutation(PERMISSIONS.NOTIFICATIONS_UPDATE, (_input: void, options) => settingsClient.createTestNotification(options)),
  };
}
